import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "ticketino-api-client";
import { UpdateTicketAddress, GetAddressOfOrganizer, UpdateBuyerAddress, UpdateOrderDetails, GetOptionsOfOrder, CreateOptionsOfOrder, UpdateOptionsOfOrder } from "../utils/api";
import { useLanguage } from "../hooks/useLanguage";
import { useOrder } from "../hooks/useOrder";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";

const Information = () => {
    const [regularToken, setRegularToken] = useState(null);
    const [bookingType, setBookingType] = useState(null);
    const [resources, setResources] = useState({});

    const [formError, setFormError] = useState("");

    const { language } = useLanguage();

    const organizerId = localStorage.getItem("loggedInOrganizerId");

    const [orderOption, setOrderOption] = useState({
        organizerId: 0,
        contactFirstName: "",
        contactLastName: "",
        contactPhone: "",
        birthdayName: "",
        birthdayAge: "",
        schoolGradeLevel: "",
        groupDescription: "",
        remark: "",
    });

    //for group booking type only defined in the addOns page
    const hasKombiTicket = localStorage.getItem("hasKombiTicket");

    //for birthday only defined in the addOns page
    const hasBlackTapMilkshake = localStorage.getItem("hasBlackTapMilkshake");

    //for all booking types - tour selected in addOns page
    const selectedTourName = localStorage.getItem("selectedTourName");

    //for all booking types - City Rally tickets selected in tickets page
    const selectedCityRallyInfo = localStorage.getItem("selectedCityRallyInfo");

    //for all booking types - Host selected in addOns page
    const selectedHostInfo = localStorage.getItem("selectedHostInfo");

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    // Helper function to convert booking type to German
    const getBookingTypeGerman = (bookingType) => {
        const bookingTypeMap = {
            'birthday': 'Kindergeburtstag',
            'schoolgroup': 'Schulgruppen',
            'groupvisit': 'Gruppenbesuch'
        };
        return bookingTypeMap[bookingType] || bookingType;
    };

    useEffect(() => {
        setRegularToken(localStorage.getItem("token"));
        setBookingType(localStorage.getItem("bookingType"));
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const validateInputFields = () => {
        const genericError = resources?.ErrorPleaseFillMandatoryFields;

        if (!orderOption) return { valid: false, error: null };
        if (!orderOption.contactFirstName || orderOption.contactFirstName.length > 70) return { valid: false, error: genericError };
        if (!orderOption.contactLastName || orderOption.contactLastName.length > 70) return { valid: false, error: genericError };
        if (!orderOption.contactPhone || orderOption.contactPhone.length > 70) return { valid: false, error: genericError };

        if (bookingType === "birthday") {
            if (!orderOption.birthdayName || orderOption.birthdayName.length > 70) return { valid: false, error: genericError };
            if (!orderOption.birthdayAge || orderOption.birthdayAge.length > 70) return { valid: false, error: genericError };
        }

        if (bookingType === "schoolgroup") {
            if (!orderOption.schoolGradeLevel || orderOption.schoolGradeLevel.length > 70) return { valid: false, error: genericError };
        }

        if (bookingType === "groupvisit") {
            if (!orderOption.groupDescription || orderOption.groupDescription.length > 70) return { valid: false, error: genericError };
        }

        return { valid: true };
    }

    const fetchOrganizerAddress = async (buyerToken, organizerId) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrganizerAddress");
            }
            const data = await GetAddressOfOrganizer(baseUrl, "buyerToken", buyerToken, organizerId);
            return data;
        } catch (error) {
            console.error('Error fetching organizer address:', error);
        }
    };

    const handleUpdateBuyerAddress = async (e) => {
        if (e) e.preventDefault();
        setFormError("");
        const validation = validateInputFields();
        if (!validation.valid) {
            setFormError(validation.error || "Please fill all mandatory fields correctly.");
            return;
        }
        if (!order?.tickets) {
            setFormError(resources?._SessionExpired);
            return;
        };

        try {
            //const organizerAddress = await fetchOrganizerAddress(regularToken, organizerId);

            //organizerAddress.code1 = orderOption.contactFirstName;
            //organizerAddress.code2 = orderOption.contactLastName;
            //organizerAddress.code3 = orderOption.contactPhone;
            //organizerAddress.code4 = orderOption.remark;
            //organizerAddress.code5 = hasBlackTapMilkshake;
            //organizerAddress.code6 = orderOption.birthdayName;
            //organizerAddress.code7 = orderOption.birthdayAge;
            //organizerAddress.code8 = orderOption.schoolGradeLevel;
            //organizerAddress.code9 = orderOption.groupDescription;
            //organizerAddress.code12 = hasKombiTicket;

            //// update address of order with logged in user
            //await UpdateBuyerAddress(baseUrl, "token", regularToken, order.id, organizerAddress);

            // Convert bookingType to German for saving
            const bookingTypeGerman = getBookingTypeGerman(bookingType);

            // update order options with booking type for later internal WOW calendar use
            const orderOptions = {
                option1: "Vorname Kontaktperson: " + orderOption.contactFirstName,
                option2: "Nachname Kontaktperson: " + orderOption.contactLastName,
                option3: "Telefonnummer Kontaktperson: " + orderOption.contactPhone,
                option4: "Bemerkung: " + orderOption.remark,
                option5: "Reservation Black Tab Milkshake: " + hasBlackTapMilkshake,
                option6: "Name Geburtstagskind: " + orderOption.birthdayName,
                option7: "Alter Geburtstagskind: " + orderOption.birthdayAge,
                option8: "Klassenstufe: " + orderOption.schoolGradeLevel,
                option9: "Beschreibung der Gruppe: " + orderOption.groupDescription,
                option10: "Gruppentyp: " + bookingTypeGerman,
                option12: "Kombiticket Rally: " + hasKombiTicket,
                option13: "Gewählte Tour: " + (selectedTourName || "-"),
                option14: "City Rally: " + (selectedCityRallyInfo || "-"),
                option15: "Host: " + (selectedHostInfo || "-"),
            };

            // check if order options exist, then create or update accordingly
            try {
                const existingOrderOptions = await GetOptionsOfOrder(baseUrl, "token", regularToken, order.id);
                if (existingOrderOptions) {
                    // if we get here, options exist, so update them
                    await UpdateOptionsOfOrder(baseUrl, "token", regularToken, order.id, orderOptions);
                }
            } catch (error) {
                // if GET fails (404 or similar), options don't exist, so create them
                await CreateOptionsOfOrder(baseUrl, "token", regularToken, order.id, orderOptions);
            }

            for (const t of order.tickets) {
                const ticketAddressBody = {
                    option1: orderOption.contactFirstName,
                    option2: orderOption.contactLastName,
                    option3: orderOption.contactPhone,
                    option4: orderOption.remark,
                    option5: hasBlackTapMilkshake,
                    option6: orderOption.birthdayName,
                    option7: orderOption.birthdayAge,
                    option8: orderOption.schoolGradeLevel,
                    option9: orderOption.groupDescription,
                    option10: bookingTypeGerman,
                    option12: hasKombiTicket,
                    option13: selectedTourName || "-",
                    option14: selectedCityRallyInfo || "-",
                    option15: selectedHostInfo || "-",
                };

                await UpdateTicketAddress(baseUrl, "token", regularToken, t.id, ticketAddressBody);
            }

            // Update order details with tags and status
            const updateOrderDetailsBody = {
                orderId: order.id,
                remark: "",
                orderStatus: order.orderStatus,
                tags: ["group"]
            };

            await UpdateOrderDetails(baseUrl, "token", regularToken, updateOrderDetailsBody);

            navigate(`/${language}/checkout`);
        } catch (error) {
            setFormError(`${resources?._Save} failed.`);
        }
    };

    const onInputChange = (e) => {
        setOrderOption({ ...orderOption, [e.target.name]: e.target.value });
    };

    return (
        <>
            <Header language={language} pagePath="information" pageTitle={resources?.InformationTitle}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* inputs */}
                    <div className="form-group row">
                        <label htmlFor="contactFirstName" className="col-md-12 col-form-label">{resources?.InformationContactFirstName} *</label>
                        <div className="col-md-12">
                            <input
                                type="text"
                                name="contactFirstName"
                                className="form-control"
                                value={orderOption?.contactFirstName || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="contactLastName" className="col-md-12 col-form-label">{resources?.InformationContactLastName} *</label>
                        <div className="col-md-12">
                            <input
                                type="text"
                                name="contactLastName"
                                className="form-control"
                                value={orderOption?.contactLastName || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="contactPhone" className="col-md-12 col-form-label">{resources?.InformationContactPhone} *</label>
                        <div className="col-md-12">
                            <input
                                type="text"
                                name="contactPhone"
                                className="form-control"
                                value={orderOption?.contactPhone || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    {/* weitere angabe */}
                    <div className="row text-center mt-5 mb-5">
                        <div className="col-md-12">
                            <span className="title">{resources?.InformationMoreInfo}</span>
                        </div>
                    </div>

                    {/* birthday - booking type */}
                    {bookingType === "birthday" &&
                        <>
                            <div className="form-group row mt-4">
                                <label htmlFor="birthdayName" className="col-md-12 col-form-label">Name ﻿Geburtstagskind *</label>
                                <div className="col-md-12">
                                    <input
                                        type="text"
                                        name="birthdayName"
                                        className="form-control"
                                        value={orderOption?.birthdayName || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="birthdayAge" className="col-md-12 col-form-label">Alter ﻿Geburtstagskind *</label>
                                <div className="col-md-12">
                                    <input
                                        type="text"
                                        name="birthdayAge"
                                        className="form-control"
                                        value={orderOption?.birthdayAge || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                        </>
                    }

                    {/* school group - booking type */}
                    {bookingType === "schoolgroup" &&
                        <div className="form-group row">
                            <label htmlFor="schoolGradeLevel" className="col-md-12 col-form-label">Klassenstufe *</label>
                            <div className="col-md-12">
                                <input
                                    type="text"
                                    name="schoolGradeLevel"
                                    className="form-control"
                                    value={orderOption?.schoolGradeLevel || ""}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>
                    }

                    {/* group - booking type */}
                    {bookingType === "groupvisit" &&
                        <div className="form-group row">
                            <label htmlFor="groupDescription" className="col-md-12 col-form-label">Beschreibung der Gruppe *</label>
                            <div className="col-md-12">
                                <textarea
                                    style={{ height: "100%" }}
                                    name="groupDescription"
                                    className="form-control"
                                    value={orderOption?.groupDescription || ""}
                                    onChange={onInputChange}
                                    rows={4}
                                />
                            </div>
                        </div>
                    }

                    <div className="form-group row">
                        <label htmlFor="remark" className="col-md-12 col-form-label">Bemerkung</label>
                        <div className="col-md-12">
                            <textarea
                                style={{ height: "100%" }}
                                name="remark"
                                className="form-control"
                                value={orderOption?.remark || ""}
                                onChange={onInputChange}
                                rows={4}
                            />
                        </div>
                    </div>


                    {/* button */}
                    <div className="row mt-5">
                        <div className="col-6">
                            <button
                                className="btn-custom white"
                                type="submit"
                                onClick={() => navigate(-1)}
                            >
                                {resources?._Back}
                            </button>
                        </div>

                        <div className="col-6 text-end">
                            <button
                                className="btn-custom"
                                type="submit"
                                onClick={handleUpdateBuyerAddress}
                            >
                                {resources?._Next}
                            </button>

                            {formError &&
                                <div className="row text-end mt-2">
                                    <p className="text-danger fw-bold">{formError}</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>

            </div>

            <Footer language={language} />
        </>
    )
}

export default Information;